import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import type {
  SuggestionKind,
  SuggestionMatchConfidence,
} from '../settings/settings.api';

/**
 * Reporting for the interest-based event suggestion push.
 *
 * Two read-only admin endpoints that answer two different questions and must
 * not be conflated:
 *
 * - `suggestions/performance` reads the **send log**. It only knows about
 *   events we chose to suggest, so it can never tell you about a category we
 *   never suggest.
 * - `analytics/event-interest` reads the **participation log**. It counts what
 *   people joined regardless of how they found it.
 *
 * Both tables are new and start empty: the send log fills only once the
 * suggestion toggle is on, and the participation log only from the deploy of
 * the capture commit onward. Empty is the expected state, not an error.
 */

/** The five counters plus the two rates every performance breakdown carries. */
export interface SuggestionRates {
  sent: number;
  delivered: number;
  failed: number;
  opened: number;
  joined: number;
  /** Fraction in [0,1]. The API returns 0 (never NaN) when `sent` is 0. */
  openRate: number;
  /** Fraction in [0,1]. The API returns 0 (never NaN) when `sent` is 0. */
  joinRate: number;
}

/** One row per daily pass. Always contains both kinds, even at zero. */
export interface SuggestionKindRow extends SuggestionRates {
  kind: SuggestionKind;
}

/**
 * One row per match tier. Always contains both `exact` and `bridge`, even at
 * zero — the comparison between the two is the point of the whole section.
 */
export interface SuggestionConfidenceRow extends SuggestionRates {
  matchConfidence: SuggestionMatchConfidence;
}

/** Top interest labels by volume sent. No delivered/failed split here. */
export interface SuggestionLabelRow {
  label: string;
  sent: number;
  opened: number;
  joined: number;
  openRate: number;
  joinRate: number;
}

/** Top matched activities by volume sent. */
export interface SuggestionActivityRow {
  activityId: string;
  activityName: string;
  categoryName: string;
  sent: number;
  opened: number;
  joined: number;
  openRate: number;
  joinRate: number;
}

/** GET /admin/notifications/suggestions/performance */
export interface SuggestionPerformance {
  windowDays: number;
  /** How long after a push an open or a join still counts as attributed. */
  attributionDays: number;
  totals: SuggestionRates;
  byKind: SuggestionKindRow[];
  byConfidence: SuggestionConfidenceRow[];
  topLabels: SuggestionLabelRow[];
  topActivities: SuggestionActivityRow[];
}

/**
 * One of the ten known interest labels.
 *
 * `matchedDeclaredInterest` counts the joins made by users who declare this
 * interest — the gap against `joins` is the gap between what people say they
 * like and what they actually attend. The backend compares against each
 * user's interests **as they are now**, not as they were at join time.
 */
export interface InterestLabelRow {
  label: string;
  joins: number;
  /** Fraction in [0,1] of all participations in the window. */
  share: number;
  matchedDeclaredInterest: number;
  /** Fraction in [0,1]: `matchedDeclaredInterest` over `joins`. */
  matchedShare: number;
}

/**
 * Joins per tagged activity. Only rows whose event carried activities can
 * appear here, and ingested events carry none — see `coverage`.
 */
export interface InterestActivityRow {
  activityId: string;
  activityName: string;
  categoryName: string;
  joins: number;
  share: number;
}

/** Joins per taxonomy category (the coarser grouping above activities). */
export interface InterestCategoryRow {
  categoryName: string;
  joins: number;
  share: number;
}

/**
 * How much of the participation log is actually usable.
 *
 * `rowsWithActivities` is expected to be a small minority: ingested events
 * carry no activities at all, so `byActivity` describes the tagged corner of
 * the catalogue, not the catalogue.
 */
export interface InterestCoverage {
  participationRows: number;
  rowsWithSnapshot: number;
  rowsWithActivities: number;
}

/** GET /admin/analytics/event-interest */
export interface EventInterestAnalytics {
  windowDays: number;
  /** ISO timestamp of the oldest row, or null when the table is empty. */
  startedCollectingAt: string | null;
  totalParticipations: number;
  byLabel: InterestLabelRow[];
  byActivity: InterestActivityRow[];
  byTaxonomyCategory: InterestCategoryRow[];
  /**
   * Joins whose event category mapped to none of the ten known labels. This
   * is the size of the blind spot in the interest taxonomy, not a rounding
   * error.
   */
  unmappedCategoryJoins: number;
  coverage: InterestCoverage;
}

export const analyticsApi = createApi({
  reducerPath: 'analyticsApi',
  baseQuery,
  endpoints: (builder) => ({
    getSuggestionPerformance: builder.query<
      SuggestionPerformance,
      { days: number; attributionDays: number }
    >({
      query: ({ days, attributionDays }) => ({
        url: `/admin/notifications/suggestions/performance?days=${days}&attributionDays=${attributionDays}`,
      }),
    }),
    getEventInterest: builder.query<EventInterestAnalytics, { days: number }>({
      query: ({ days }) => ({
        url: `/admin/analytics/event-interest?days=${days}`,
      }),
    }),
  }),
});

export const {
  useGetSuggestionPerformanceQuery,
  useGetEventInterestQuery,
} = analyticsApi;
